import React, { useCallback, useEffect, useState, useMemo } from 'react'

export interface Pagination {
  items: any[]
  handlePageSelected: (
    page: number,
    event: React.ChangeEvent<HTMLInputElement>
  ) => void
  subset: any[]
  pageCount: number
  selectedPage: number
  onFirstPage: boolean
  onLastPage: boolean
  pageSize: number
  changePage: (page: number) => void
}

export function usePagination(
  data = [],
  itemsPerPage = 10,
  initialPage = 0,
  loading = false
): { pagination: Pagination } {
  // page is zero-based index
  const [page, setPage] = useState(initialPage)
  const pageSize = itemsPerPage
  // ignore any input if it's marked as loading
  const items = useMemo(() => (loading ? [] : data), [loading, data])
  const pageCount = Math.ceil(items.length / pageSize)

  /**
   * Function used to set the current selected page based on the pagecount
   *
   * @param {integer} selectedItem
   */
  const changePage = useCallback(
    (selectedItem) => {
      if (selectedItem < 0) {
        setPage(0)
        return
      }

      const newPage = selectedItem > pageCount ? pageCount - 1 : selectedItem
      setPage(newPage)

      return newPage
    },
    [pageCount, setPage]
  )

  /**
   * Function used to handle click on the pagination number links
   *
   * @param {integer} selected
   * @param {object} event
   */
  const handlePageSelected = (selected, event) => {
    if (event) {
      event.preventDefault
        ? event.preventDefault()
        : (event.returnValue = false)
    }
    if (page === selected) return
    changePage(selected)
  }

  // reset to last page of results if data set changes to have fewer results
  // and the page we're on no longer exists
  useEffect(() => {
    if (page >= pageCount && !loading) {
      setPage(pageCount > 0 ? pageCount - 1 : 0)
    }
  }, [loading, page, pageCount])

  return {
    pagination: {
      items,
      handlePageSelected,
      subset: items.slice(page * pageSize, page * pageSize + pageSize),
      pageCount,
      selectedPage: page,
      onFirstPage: page <= 0,
      onLastPage: pageCount === 1 || page >= pageCount - 1,
      pageSize,
      changePage
    }
  }
}
